import { Component, OnInit } from '@angular/core';
import {
  AccountV2Service,
  ForYouCard,
  ForYouResponse,
  MatchReason,
  UserInterest,
} from 'src/app/services/account-v2-service';

/**
 * Member "For You" — a calm white editorial module (Phase 5 Step 2). LIVE:
 * GET /account/for-you. Every card's "because" line is rendered from the
 * deterministic MatchReason — no free text from the server. Includes a
 * minimal interests control (add/remove) so the loop is user-facing end to
 * end. The auction fixture lives in the "Your market" section (home-market).
 */
@Component({
  selector: 'pla-home-for-you',
  templateUrl: './home-for-you.component.html',
  styleUrls: ['./home-for-you.component.scss'],
})
export class HomeForYouComponent implements OnInit {
  public forYou: ForYouResponse | null = null;
  public interests: UserInterest[] = [];
  public failed = false;

  public newType = 'initials';
  public newValue = '';
  public interestTypes = ['name', 'initials', 'number', 'format', 'collection'];

  constructor(private account: AccountV2Service) {}

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.account.getForYou().subscribe({
      next: (r) => (this.forYou = r),
      error: () => (this.failed = true),
    });
    this.account.getInterests().subscribe({
      next: (i) => (this.interests = i),
      error: () => (this.interests = []),
    });
  }

  public addInterest(): void {
    const value = (this.newValue || '').trim();
    if (!value) return;
    this.account.addInterest(this.newType, value).subscribe({
      next: () => {
        this.newValue = '';
        this.load(); // re-match: For You reflects the new interest
      },
      error: () => {},
    });
  }

  public removeInterest(id: number): void {
    this.account.removeInterest(id).subscribe({ next: () => this.load(), error: () => {} });
  }

  public plateUrl(reg: string): string {
    return `/plates/${(reg || '').replace(/\s/g, '')}`;
  }

  public priceLabel(card: ForYouCard): string {
    if (card.pricePence == null) return '';
    return `£${Math.round(card.pricePence / 100).toLocaleString('en-GB')}`;
  }

  /**
   * Deterministic "because" copy from the structured reason — a fixed
   * template registry, no server prose. Mirrors the closed MatchRules set.
   */
  public becauseLine(reason: MatchReason): string {
    switch (reason.rule) {
      case 'owned_registration':
        return 'In your collection';
      case 'followed_registration':
        return 'A plate you follow';
      case 'saved_search_registration':
      case 'saved_search_name':
      case 'saved_search_initials':
      case 'saved_search_number':
      case 'saved_search_format':
      case 'saved_search_collection':
        return `From your saved search`;
      case 'interest_name_arrangement':
        return `Your ${reason.value} interest`;
      case 'interest_initials_containment':
        return `Your ${reason.value} initials interest`;
      case 'interest_number_exact':
        return `Your number ${reason.value} interest`;
      case 'interest_number_neighbour':
        return `Near your number ${reason.value} interest`;
      case 'interest_format':
        return `Your ${reason.value} interest`;
      case 'interest_collection':
        return `From your ${reason.value.replace('_', ' ')} interest`;
      default:
        return 'Matched to your market';
    }
  }

  /**
   * Short deterministic token for a reason, used only for the compact
   * "also matches" line when a lot satisfies more than one interest.
   */
  private reasonToken(r: MatchReason): string {
    switch (r.rule) {
      case 'interest_initials_containment':
        return `${r.value} initials`;
      case 'interest_number_exact':
        return `number ${r.value}`;
      case 'interest_number_neighbour':
        return `near number ${r.value}`;
      case 'interest_name_arrangement':
        return `the name ${r.value}`;
      case 'interest_format':
        return `${r.value}`;
      case 'interest_collection':
        return `${(r.value || '').replace(/_/g, ' ')}`;
      case 'owned_registration':
        return 'your collection';
      case 'followed_registration':
        return 'a plate you follow';
      default:
        return r.rule && r.rule.indexOf('saved_search') === 0
          ? 'a saved search'
          : '';
    }
  }

  /**
   * Additional matched interests beyond the primary reason, deduplicated by
   * rendered token so a lot never repeats the same copy. Empty when a lot
   * matched on a single interest — the common case.
   */
  public otherReasons(card: ForYouCard): string[] {
    const seen = new Set<string>([this.reasonToken(card.reason)]);
    const out: string[] = [];
    for (const r of card.reasons || []) {
      const token = this.reasonToken(r);
      if (token && !seen.has(token)) {
        seen.add(token);
        out.push(token);
      }
    }
    return out;
  }
}
